import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import {
  Activity,
  Eye,
  EyeOff,
  Loader2,
  RadioTower,
  RefreshCw,
  SquareArrowOutUpRight,
  X,
} from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ScrollArea } from "@/components/ui/scroll-area"
import { closeAdminRoom, getAdminRooms } from "@/lib/api"
import { getSocket } from "@/lib/socket"
import { formatDuration, decodeHtmlEntities } from "@/lib/format"

function formatRoomDuration(room) {
  const createdAt = room?.created_at ? new Date(room.created_at) : null
  if (!createdAt || Number.isNaN(createdAt.getTime())) return "0:00"
  const endAt = room?.status === "closed" && room?.closed_at ? new Date(room.closed_at) : new Date()
  if (Number.isNaN(endAt.getTime())) return "0:00"
  return formatDuration(Math.max(0, Math.floor((endAt - createdAt) / 1000)))
}

function formatSong(song) {
  if (!song) return "Idle"
  const title = decodeHtmlEntities(song.title || "Untitled")
  const requestedBy = song.requested_by_name ? ` · ${song.requested_by_name}` : ""
  return `${title}${requestedBy}`
}

function mergeRoom(prevRooms, roomCode, updater) {
  return prevRooms.map((room) => {
    if ((room.room_code || "").toUpperCase() !== roomCode.toUpperCase()) return room
    const next = typeof updater === "function" ? updater(room) : updater
    return { ...room, ...next }
  })
}

function roomStatusLabel(room) {
  if (room?.status === "closed") return room.closed_reason ? `Closed · ${room.closed_reason}` : "Closed"
  if (room?.host_connected === false) return "Host offline"
  return "Live"
}

export default function RoomsPage() {
  const [rooms, setRooms] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState("")
  const [socketConnected, setSocketConnected] = useState(false)
  const [selectedRoomCode, setSelectedRoomCode] = useState("")
  const [selectedMode, setSelectedMode] = useState("inspect")
  const [closingByCode, setClosingByCode] = useState({})
  const [eventLog, setEventLog] = useState([])
  const watchedCodesRef = useRef([])
  const roomsRef = useRef([])
  const selectedRoomCodeRef = useRef("")

  useEffect(() => {
    roomsRef.current = rooms
  }, [rooms])

  useEffect(() => {
    selectedRoomCodeRef.current = selectedRoomCode
  }, [selectedRoomCode])

  const selectedRoom = rooms.find((room) => room.room_code === selectedRoomCode) || null

  const pushEvent = useCallback((kind, roomCode, detail) => {
    setEventLog((prev) => [
      {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        kind,
        roomCode,
        detail,
        at: new Date().toLocaleTimeString(),
      },
      ...prev,
    ].slice(0, 24))
  }, [])

  const syncWatchedRooms = useCallback((roomCodes) => {
    const nextCodes = Array.from(new Set(roomCodes.map((code) => String(code || "").toUpperCase()).filter(Boolean)))
    const previousCodes = watchedCodesRef.current
    const toAdd = nextCodes.filter((code) => !previousCodes.includes(code))
    const toRemove = previousCodes.filter((code) => !nextCodes.includes(code))
    const sock = getSocket()

    if (toAdd.length) sock.emit("admin:watchRooms", { room_codes: toAdd })
    if (toRemove.length) sock.emit("admin:unwatchRooms", { room_codes: toRemove })
    watchedCodesRef.current = nextCodes
  }, [])

  const applyRoomUpdate = useCallback((payload, fallbackKind) => {
    const roomCode = (payload?.room_code || payload?.room?.room_code || "").toUpperCase()
    if (!roomCode) return

    setRooms((prev) => {
      const existing = prev.find((room) => room.room_code === roomCode)
      if (!existing && fallbackKind === "closed") return prev

      let nextRooms = prev
      if (!existing && payload?.room) {
        nextRooms = [payload.room, ...prev]
      } else {
        nextRooms = mergeRoom(prev, roomCode, (room) => ({
          ...(payload?.room || {}),
          ...(payload?.guest_count ? { participants_count: payload.guest_count } : {}),
        }))
      }

      if (payload?.guests) {
        nextRooms = nextRooms.map((room) =>
          room.room_code === roomCode ? { ...room, participants_count: payload.guests.length } : room
        )
      }
      if (payload?.queue) {
        nextRooms = nextRooms.map((room) =>
          room.room_code === roomCode
            ? {
                ...room,
                queue_length: payload.queue.length,
                current_song: payload.queue[0] || null,
              }
            : room
        )
      }
      if (fallbackKind === "closed") {
        nextRooms = nextRooms.filter((room) => room.room_code !== roomCode)
      }
      return nextRooms
    })

    if (fallbackKind === "closed") {
      pushEvent("room:closed", roomCode, payload?.reason || "closed")
      if (selectedRoomCodeRef.current === roomCode) {
        setSelectedRoomCode("")
      }
      return
    }

    if (fallbackKind === "guest") {
      pushEvent("guest:update", roomCode, `${payload?.guests?.length || 0} connected guests`)
      return
    }

    if (fallbackKind === "queue") {
      pushEvent("queue:update", roomCode, `${payload?.queue?.length || 0} queued songs`)
      return
    }

    if (fallbackKind === "player") {
      pushEvent("player:update", roomCode, payload?.state || "updated")
      return
    }

    pushEvent("room:update", roomCode, "room state refreshed")
  }, [pushEvent])

  const loadRooms = useCallback(async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true)
    } else {
      setLoading(true)
    }
    setError("")
    try {
      const data = await getAdminRooms()
      const nextRooms = Array.isArray(data?.items) ? data.items : []
      setRooms(nextRooms)
      syncWatchedRooms(nextRooms.map((room) => room.room_code))
      if (selectedRoomCodeRef.current && !nextRooms.some((room) => room.room_code === selectedRoomCodeRef.current)) {
        setSelectedRoomCode("")
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load rooms")
      if (!silent) {
        setRooms([])
      }
    } finally {
      if (silent) {
        setRefreshing(false)
      } else {
        setLoading(false)
      }
    }
  }, [syncWatchedRooms])

  useEffect(() => {
    loadRooms()
    const timer = window.setInterval(() => {
      loadRooms({ silent: true })
    }, 15000)
    return () => window.clearInterval(timer)
  }, [loadRooms])

  useEffect(() => {
    const sock = getSocket()

    const onConnect = () => {
      setSocketConnected(true)
      syncWatchedRooms(roomsRef.current.map((room) => room.room_code))
    }
    const onDisconnect = () => setSocketConnected(false)
    const onRoomUpdate = (payload) => applyRoomUpdate(payload, "room")
    const onQueueUpdate = (payload) => applyRoomUpdate(payload, "queue")
    const onGuestUpdate = (payload) => applyRoomUpdate(payload, "guest")
    const onPlayerUpdate = (payload) => applyRoomUpdate(payload, "player")
    const onRoomClosed = (payload) => applyRoomUpdate(payload, "closed")

    sock.on("connect", onConnect)
    sock.on("disconnect", onDisconnect)
    sock.on("room:update", onRoomUpdate)
    sock.on("queue:update", onQueueUpdate)
    sock.on("guest:update", onGuestUpdate)
    sock.on("player:update", onPlayerUpdate)
    sock.on("room:closed", onRoomClosed)

    if (sock.connected) onConnect()

    return () => {
      sock.off("connect", onConnect)
      sock.off("disconnect", onDisconnect)
      sock.off("room:update", onRoomUpdate)
      sock.off("queue:update", onQueueUpdate)
      sock.off("guest:update", onGuestUpdate)
      sock.off("player:update", onPlayerUpdate)
      sock.off("room:closed", onRoomClosed)
    }
  }, [applyRoomUpdate, syncWatchedRooms])

  useEffect(() => {
    syncWatchedRooms(rooms.map((room) => room.room_code))
    roomsRef.current = rooms
  }, [rooms, syncWatchedRooms])

  const metrics = useMemo(() => {
    const activeRooms = rooms.length
    const participants = rooms.reduce((total, room) => total + Number(room.participants_count || 0), 0)
    const queues = rooms.reduce((total, room) => total + Number(room.queue_length || 0), 0)
    const hostOffline = rooms.filter((room) => room.host_connected === false).length
    return { activeRooms, participants, queues, hostOffline }
  }, [rooms])

  const handleInspect = (room) => {
    setSelectedRoomCode(room.room_code)
    setSelectedMode("inspect")
    pushEvent("inspect", room.room_code, room.room_name || room.room_code)
  }

  const handleObserve = (room) => {
    setSelectedRoomCode(room.room_code)
    setSelectedMode("observe")
    pushEvent("observe", room.room_code, "watching live socket updates")
  }

  const handleEndRoom = async (room) => {
    setClosingByCode((prev) => ({ ...prev, [room.room_code]: true }))
    try {
      await closeAdminRoom(room.room_code)
      pushEvent("close", room.room_code, "close requested")
      toast.success(`Closed room ${room.room_code}`)
      const nextRooms = roomsRef.current.filter((item) => item.room_code !== room.room_code)
      setRooms(nextRooms)
      if (selectedRoomCodeRef.current === room.room_code) {
        setSelectedRoomCode("")
      }
      syncWatchedRooms(nextRooms.map((item) => item.room_code))
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to close room")
    } finally {
      setClosingByCode((prev) => ({ ...prev, [room.room_code]: false }))
    }
  }

  return (
    <div className="space-y-6">
      <Card className="glass border-white/5 overflow-hidden">
        <CardContent className="p-6 sm:p-8 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300 mb-2">Developer Console</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-[Outfit] leading-tight">Room Monitoring</h2>
            <p className="text-zinc-400 mt-2 max-w-3xl">
              Live rooms from the existing Socket.IO room lifecycle. The table refreshes from the admin API and watches the same room events used by the host and guest views.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="border-white/10 bg-white/5 text-zinc-200">
              {socketConnected ? "Socket live" : "Socket reconnecting"}
            </Badge>
            <Button type="button" variant="outline" className="border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10" onClick={() => loadRooms({ silent: true })} disabled={loading || refreshing}>
              {loading || refreshing ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="glass border-white/5">
          <CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Active Rooms</div>
            <div className="text-3xl font-bold text-white font-[Outfit]">{metrics.activeRooms}</div>
          </CardContent>
        </Card>
        <Card className="glass border-white/5">
          <CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Participants</div>
            <div className="text-3xl font-bold text-white font-[Outfit]">{metrics.participants}</div>
          </CardContent>
        </Card>
        <Card className="glass border-white/5">
          <CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Queued Songs</div>
            <div className="text-3xl font-bold text-white font-[Outfit]">{metrics.queues}</div>
          </CardContent>
        </Card>
        <Card className="glass border-white/5">
          <CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Host Offline</div>
            <div className="text-3xl font-bold text-white font-[Outfit]">{metrics.hostOffline}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(320px,0.9fr)]">
        <Card className="glass border-white/5 overflow-hidden">
          <CardHeader className="p-6 pb-3">
            <CardTitle className="text-sm uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-2">
              <RadioTower size={14} />
              Active Rooms
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="px-6 pb-6 text-zinc-400 flex items-center gap-3">
                <Loader2 className="animate-spin" size={16} />
                Loading rooms...
              </div>
            ) : error ? (
              <div className="px-6 pb-6 text-sm text-red-200">{error}</div>
            ) : rooms.length === 0 ? (
              <div className="px-6 pb-6 text-zinc-400">No active rooms found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left">
                  <thead className="bg-white/[0.02] text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    <tr>
                      <th className="px-6 py-4 font-medium">Room Code</th>
                      <th className="px-6 py-4 font-medium">Host</th>
                      <th className="px-6 py-4 font-medium">Participants</th>
                      <th className="px-6 py-4 font-medium">Current Song</th>
                      <th className="px-6 py-4 font-medium">Queue Length</th>
                      <th className="px-6 py-4 font-medium">Room Duration</th>
                      <th className="px-6 py-4 font-medium">Status</th>
                      <th className="px-6 py-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rooms.map((room) => {
                      const selected = room.room_code === selectedRoomCode
                      return (
                        <tr
                          key={room.room_code}
                          className={selected ? "bg-purple-500/10" : "border-t border-white/5 hover:bg-white/[0.02]"}
                        >
                          <td className="px-6 py-4">
                            <div className="font-mono text-sm font-semibold text-white">{room.room_code}</div>
                            <div className="text-xs text-zinc-500">{room.room_name || "Unnamed room"}</div>
                          </td>
                          <td className="px-6 py-4 text-zinc-200">{room.host_nickname || "Unknown"}</td>
                          <td className="px-6 py-4 text-zinc-200">{room.participants_count ?? 0}</td>
                          <td className="px-6 py-4 text-zinc-200 max-w-[280px] truncate">{formatSong(room.current_song)}</td>
                          <td className="px-6 py-4 text-zinc-200">{room.queue_length ?? 0}</td>
                          <td className="px-6 py-4 text-zinc-200">{formatRoomDuration(room)}</td>
                          <td className="px-6 py-4">
                            <Badge variant="outline" className="border-white/10 bg-white/5 text-zinc-200">
                              {roomStatusLabel(room)}
                            </Badge>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-2">
                              <Button type="button" size="sm" variant="outline" className="border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10" onClick={() => handleInspect(room)}>
                                <SquareArrowOutUpRight size={14} />
                                Inspect
                              </Button>
                              <Button type="button" size="sm" variant="outline" className="border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10" onClick={() => handleObserve(room)}>
                                <Eye size={14} />
                                Observe
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                className="bg-red-500/90 text-white hover:bg-red-500"
                                onClick={() => handleEndRoom(room)}
                                disabled={closingByCode[room.room_code]}
                              >
                                {closingByCode[room.room_code] ? <Loader2 className="animate-spin" size={14} /> : <X size={14} />}
                                End Room
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="glass border-white/5 overflow-hidden">
          <CardHeader className="p-6 pb-3">
            <CardTitle className="text-sm uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-2">
              <Activity size={14} />
              {selectedRoom ? `${selectedMode === "observe" ? "Observe" : "Inspect"} Room` : "Room Details"}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            {!selectedRoom ? (
              <div className="rounded-3xl border border-white/5 bg-zinc-900/40 p-6 text-zinc-400 min-h-[480px] flex flex-col justify-center gap-4">
                <div className="text-white font-semibold text-lg font-[Outfit]">Choose a room to inspect</div>
                <p>
                  Inspect opens a detailed monitoring view. Observe keeps the focus on live event flow from the current Socket.IO updates.
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-white/5 bg-black/20 p-4">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Socket mode</div>
                    <div className="text-zinc-200">{socketConnected ? "Connected" : "Waiting for reconnect"}</div>
                  </div>
                  <div className="rounded-2xl border border-white/5 bg-black/20 p-4">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Watched rooms</div>
                    <div className="text-zinc-200">{watchedCodesRef.current.length}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-3xl border border-white/5 bg-zinc-900/40 p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300 mb-2">{selectedMode === "observe" ? "Observe Mode" : "Inspect Mode"}</div>
                      <div className="text-3xl font-bold text-white font-[Outfit]">{selectedRoom.room_code}</div>
                      <div className="text-zinc-400 mt-1">{selectedRoom.room_name || "Unnamed room"}</div>
                    </div>
                    <Button type="button" variant="ghost" size="icon" className="rounded-full bg-white/5 border border-white/5 text-zinc-300 hover:bg-white/10" onClick={() => setSelectedRoomCode("") }>
                      <X size={16} />
                    </Button>
                  </div>
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    <div className="rounded-2xl border border-white/5 bg-black/20 p-4">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Host</div>
                      <div className="text-white font-medium">{selectedRoom.host_nickname || "Unknown"}</div>
                    </div>
                    <div className="rounded-2xl border border-white/5 bg-black/20 p-4">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Participants</div>
                      <div className="text-white font-medium">{selectedRoom.participants_count ?? 0}</div>
                    </div>
                    <div className="rounded-2xl border border-white/5 bg-black/20 p-4">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Queue Length</div>
                      <div className="text-white font-medium">{selectedRoom.queue_length ?? 0}</div>
                    </div>
                    <div className="rounded-2xl border border-white/5 bg-black/20 p-4">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Duration</div>
                      <div className="text-white font-medium">{formatRoomDuration(selectedRoom)}</div>
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-3xl border border-white/5 bg-zinc-900/40 p-5">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Current Song</div>
                    <div className="text-xl font-semibold text-white">
                      {selectedRoom.current_song ? decodeHtmlEntities(selectedRoom.current_song.title || "Untitled") : "Idle"}
                    </div>
                    <div className="text-sm text-zinc-400 mt-2">
                      {selectedRoom.current_song?.requested_by_name || "No queued song is playing"}
                    </div>
                  </div>

                  <div className="rounded-3xl border border-white/5 bg-zinc-900/40 p-5">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Status</div>
                    <Badge variant="outline" className="border-white/10 bg-white/5 text-zinc-200">
                      {roomStatusLabel(selectedRoom)}
                    </Badge>
                    <div className="mt-3 text-sm text-zinc-400 flex items-center gap-2">
                      {selectedRoom.host_connected === false ? <EyeOff size={14} /> : <Eye size={14} />}
                      {selectedRoom.host_connected === false ? "Host connection is offline" : "Host connection is active"}
                    </div>
                  </div>
                </div>

                <div className="rounded-3xl border border-white/5 bg-zinc-900/40 p-5">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Live Events</div>
                    <div className="text-xs text-zinc-500">Existing Socket.IO events only</div>
                  </div>
                  <ScrollArea className="h-[220px] pr-4">
                    <div className="space-y-2">
                      {eventLog.length === 0 ? (
                        <div className="text-sm text-zinc-500">Waiting for the first room update...</div>
                      ) : (
                        eventLog.map((event) => (
                          <div key={event.id} className="rounded-2xl border border-white/5 bg-black/20 px-4 py-3">
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2 text-sm text-white">
                                <Activity size={14} className="text-purple-300" />
                                {event.kind}
                              </div>
                              <div className="text-xs text-zinc-500">{event.at}</div>
                            </div>
                            <div className="mt-1 text-xs text-zinc-400 font-mono">{event.roomCode}</div>
                            <div className="mt-1 text-sm text-zinc-300">{String(event.detail || "")}</div>
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}